import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, UserRound, ShoppingBag, Phone, MapPin, Instagram, Facebook } from "lucide-react";
import { storePath } from "../../lib/storeUrl";
import { getCart, cartCount } from "../../lib/cart";
import { getCustomerInfo } from "../../lib/customer";
import { fileUrl } from "../../lib/api";
import { headingFont } from "../StoreKit";

/* Shared store chrome (header + footer) for store-scoped pages that live outside the
   main Storefront component (customer account/login, etc.). Fully theme-driven via the
   same CSS variables, header_style and storePath routing — never links to the platform.
   All links resolve inside the current store (custom domain + platform host safe). */

const headerStyleOf = (HS) =>
  HS === "solid" ? { backgroundColor: "var(--sp)", color: "#fff" }
  : HS === "dark" ? { backgroundColor: "#0B1220", color: "#fff" }
  : HS === "gradient" ? { backgroundImage: "linear-gradient(90deg, var(--sp), var(--sa))", color: "#fff" }
  : HS === "minimal" ? { backgroundColor: "var(--sbg)", color: "var(--sp)", borderBottom: "1px solid rgba(0,0,0,0.08)" }
  : HS === "centered" ? { backgroundColor: "var(--sbg)", color: "var(--sp)", borderBottom: "2px solid var(--sp)" }
  : { backgroundColor: "rgba(255,255,255,0.85)", color: "var(--sp)", backdropFilter: "blur(16px)" };

export function StoreHeader({ slug, store = {}, info = {}, branding = {}, theme, categories = [] }) {
  const [mobileNav, setMobileNav] = useState(false);
  const head = headingFont(theme);
  const HS = theme?.header_style || "glass";
  const headerTextIsLight = ["solid", "dark", "gradient"].includes(HS);
  const cust = getCustomerInfo(slug);
  const count = cartCount(getCart(slug));
  const shopTo = (c) => storePath(slug, "/shop") + (c ? `?cat=${encodeURIComponent(c)}` : "");
  const logo = info.logo_url || branding.logo_url;

  return (
    <header className="sticky top-0 z-40 shadow-sm" style={headerStyleOf(HS)} data-testid="store-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
        <button className="md:hidden h-9 w-9 rounded-full flex items-center justify-center" onClick={() => setMobileNav(!mobileNav)} data-testid="store-mobile-menu" aria-label="Menu">
          {mobileNav ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
        <Link to={storePath(slug, "")} className="flex items-center gap-2.5 min-w-0" data-testid="store-logo-link">
          {logo ? (
            <img src={fileUrl(logo)} alt={store.name || "Store"} className="h-9 object-contain shrink-0" data-testid="storefront-logo" />
          ) : (
            <div className="h-9 w-9 rounded-lg flex items-center justify-center font-display font-extrabold text-sm shrink-0"
              style={{ backgroundColor: "var(--sa)", color: headerTextIsLight ? "var(--sp)" : "#fff" }}>
              {(store.name || "S")[0]}
            </div>
          )}
          <span data-testid="storefront-name" className={`${head} text-lg tracking-tight truncate`}>{store.name}</span>
        </Link>
        <nav className="hidden md:flex items-center gap-1 mx-auto">
          <Link to={shopTo("")} data-testid="nav-shop" className={`px-3.5 py-2 rounded-full text-sm font-bold transition-colors ${headerTextIsLight ? "hover:bg-white/15" : "hover:bg-black/5"}`}>Shop</Link>
          {categories.slice(0, 4).map((c) => (
            <Link key={c} to={shopTo(c)} data-testid={`nav-cat-${c.toLowerCase()}`} className={`px-3.5 py-2 rounded-full text-sm font-bold transition-colors ${headerTextIsLight ? "hover:bg-white/15" : "hover:bg-black/5"}`}>{c}</Link>
          ))}
        </nav>
        <div className="ml-auto md:ml-0 flex items-center gap-1.5">
          <Link to={storePath(slug, "/account")} data-testid="account-button"
            className={`h-10 px-3 rounded-full flex items-center gap-1.5 text-sm font-bold transition-colors ${headerTextIsLight ? "hover:bg-white/15" : "hover:bg-black/5"}`}>
            <UserRound className="h-4 w-4" /> <span className="hidden md:inline">{cust?.name ? cust.name.split(" ")[0] : "Login"}</span>
          </Link>
          {info.phone && (
            <a data-testid="storefront-call" href={`tel:${info.phone}`} className={`hidden sm:flex h-10 w-10 rounded-full items-center justify-center transition-colors ${headerTextIsLight ? "hover:bg-white/15" : "hover:bg-black/5"}`}>
              <Phone className="h-4 w-4" />
            </a>
          )}
          <Link to={storePath(slug, "/cart")} data-testid="cart-button"
            className="relative h-10 px-4 rounded-full flex items-center gap-2 font-bold text-sm transition-transform active:scale-95"
            style={{ backgroundColor: "var(--sa)", color: "#fff" }}>
            <ShoppingBag className="h-4 w-4" /> <span className="hidden sm:inline">Cart</span>
            {count > 0 && <span data-testid="cart-count" className="h-5 min-w-5 px-1 rounded-full bg-white text-[10px] font-extrabold flex items-center justify-center" style={{ color: "var(--sp)" }}>{count}</span>}
          </Link>
        </div>
      </div>
      {mobileNav && (
        <nav className={`md:hidden overflow-hidden border-t ${headerTextIsLight ? "border-white/10" : "border-black/5"}`} data-testid="store-mobile-panel">
          <div className="px-4 py-4 space-y-1 max-h-[70vh] overflow-y-auto">
            <p className="px-3 pb-1 text-[10px] font-extrabold uppercase tracking-widest opacity-50">Shop</p>
            <Link to={shopTo("")} onClick={() => setMobileNav(false)} data-testid="mobile-cat-all"
              className="block px-4 py-3 rounded-xl text-sm font-bold hover:bg-black/5 transition-colors">All Products</Link>
            {categories.map((c) => (
              <Link key={c} to={shopTo(c)} onClick={() => setMobileNav(false)} data-testid={`mobile-cat-${c.toLowerCase()}`}
                className="block px-4 py-3 rounded-xl text-sm font-bold hover:bg-black/5 transition-colors">{c}</Link>
            ))}
            <p className="px-3 pt-3 pb-1 text-[10px] font-extrabold uppercase tracking-widest opacity-50">Account</p>
            <Link to={storePath(slug, "/account")} onClick={() => setMobileNav(false)} data-testid="mobile-account-link"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold hover:bg-black/5 transition-colors">
              <UserRound className="h-4 w-4" /> {cust?.name ? `My Account (${cust.name.split(" ")[0]})` : "Login / My Account"}
            </Link>
            <Link to={storePath(slug, "/cart")} onClick={() => setMobileNav(false)} data-testid="mobile-cart-link"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold hover:bg-black/5 transition-colors">
              <ShoppingBag className="h-4 w-4" /> Cart{count > 0 ? ` (${count})` : ""}
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}

export function StoreFooter({ slug, store = {}, info = {}, branding = {}, theme, categories = [] }) {
  const head = headingFont(theme);
  const shopTo = (c) => storePath(slug, "/shop") + (c ? `?cat=${encodeURIComponent(c)}` : "");
  return (
    <footer style={{ backgroundColor: "var(--sp)" }} className="text-white" data-testid="store-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <p className={`${head} text-xl`}>{store.name}</p>
          {info.description && <p className="text-sm text-white/60 mt-3 leading-relaxed">{info.description}</p>}
          <div className="flex gap-2 mt-4">
            {info.socials?.instagram && <a data-testid="social-instagram" href={`https://instagram.com/${info.socials.instagram.replace("@", "")}`} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"><Instagram className="h-4 w-4" /></a>}
            {info.socials?.facebook && <a data-testid="social-facebook" href={`https://facebook.com/${info.socials.facebook}`} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"><Facebook className="h-4 w-4" /></a>}
          </div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-widest font-extrabold text-white/50 mb-4">Shop</p>
          <div className="space-y-2.5">
            <Link to={shopTo("")} data-testid="footer-shop-all" className="block text-sm text-white/75 hover:text-white transition-colors">All Products</Link>
            {categories.slice(0, 4).map((c) => (
              <Link key={c} to={shopTo(c)} data-testid={`footer-shop-${c.toLowerCase()}`} className="block text-sm text-white/75 hover:text-white transition-colors">{c}</Link>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-widest font-extrabold text-white/50 mb-4">Account</p>
          <div className="space-y-2.5">
            <Link to={storePath(slug, "/account")} data-testid="footer-account" className="block text-sm text-white/75 hover:text-white transition-colors">My Account / Orders</Link>
            <Link to={storePath(slug, "/cart")} data-testid="footer-cart" className="block text-sm text-white/75 hover:text-white transition-colors">Cart</Link>
            <Link to={storePath(slug, "/checkout")} data-testid="footer-checkout" className="block text-sm text-white/75 hover:text-white transition-colors">Checkout</Link>
          </div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-widest font-extrabold text-white/50 mb-4">Contact</p>
          <div className="space-y-2.5 text-sm text-white/75">
            {info.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />{info.phone}</p>}
            {info.email && <p>{info.email}</p>}
            {info.address && <p className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />{info.address}</p>}
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-white/40">
        © {new Date().getFullYear()} {store.name} · {branding.footer_text || (branding.brand_name ? `Powered by ${branding.brand_name}` : "Powered by MILTROO")}
      </div>
    </footer>
  );
}
